import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Address, encodeFunctionData, formatEther, isAddress, parseAbi, zeroAddress } from "viem";
import { useToast } from "@/components/ui/use-toast";
import { INCEPTION_CREATE_CALLDATA, INCEPTION_FACTORY_ADDRESS } from "@/features/inception/config";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Archive } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { usePublicClient } from "wagmi";
import { polygon } from "viem/chains";
import {
  buildAssetTransferAction,
  ReadClient,
  TransferAssetType,
} from "./assetTransfer";
import {
  encodeDomainRegistration,
  MAX_DOMAIN_RECORD_BYTES,
  POLYGON_NAME_SERVICE_ABI,
  POLYGON_NAME_SERVICE_ADDRESS,
  POLYGON_NAME_TLD,
  validateDomainLabel,
} from "@/features/domains/config";
import { NFT_MARKETPLACE_ABI, NFT_MARKETPLACE_ADDRESS } from "@/features/nft/config";
import {
  buildMarketplaceGovernanceAction,
  MarketplaceGovernanceActionType,
} from "./marketplaceGovernance";

type ProposalType = "transfer" | "action" | "discussion" | "inception" | "domain" | "marketplace";

const GOVERNANCE_NO_OP_CALLDATA = encodeFunctionData({
  abi: parseAbi(["function proposalThreshold() view returns (uint256)"]),
  functionName: "proposalThreshold",
});

interface CreateProposalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateProposal: (
    targets: Address[],
    values: bigint[],
    calldatas: `0x${string}`[],
    description: string
  ) => Promise<boolean>;
  isLoading: boolean;
  statusLabel?: string;
  governorAddress: Address;
  treasuryAddress: Address;
  tokenAddress: Address;
  tokenSymbol: string;
}

export const CreateProposalDialog = ({
  open,
  onOpenChange,
  onCreateProposal,
  isLoading,
  statusLabel,
  governorAddress,
  treasuryAddress,
  tokenAddress,
  tokenSymbol,
}: CreateProposalDialogProps) => {
  const { toast } = useToast();
  const publicClient = usePublicClient({ chainId: polygon.id });
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [targetAddress, setTargetAddress] = useState("");
  const [calldata, setCalldata] = useState("");
  const [proposalType, setProposalType] = useState<ProposalType>("action");
  const [assetType, setAssetType] = useState<TransferAssetType>("native");
  const [customAssetAddress, setCustomAssetAddress] = useState("");
  const [recipient, setRecipient] = useState("");
  const [amount, setAmount] = useState("");
  const [tokenId, setTokenId] = useState("");
  const [domainLabel, setDomainLabel] = useState("");
  const [domainRecord, setDomainRecord] = useState("");
  const [marketplaceAction, setMarketplaceAction] = useState<MarketplaceGovernanceActionType>("withdraw");
  const [marketplaceFeeRecipient, setMarketplaceFeeRecipient] = useState<string>(treasuryAddress);
  const [marketplaceFeeBps, setMarketplaceFeeBps] = useState("250");
  const [marketplaceNewOwner, setMarketplaceNewOwner] = useState("");
  const proposalTextOptional = proposalType === "transfer" || proposalType === "marketplace";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!proposalTextOptional && (!title.trim() || !description.trim())) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    let targets: Address[];
    let values: bigint[];
    let calldatas: `0x${string}`[];
    let generatedSummary = "";
    let generatedTitle = "";

    if (proposalType === "transfer") {
      if (!publicClient) {
        toast({ title: "Polygon unavailable", description: "Unable to validate the DAO treasury on Polygon.", variant: "destructive" });
        return;
      }
      try {
        const action = await buildAssetTransferAction({
          assetType,
          treasuryAddress,
          governanceTokenAddress: tokenAddress,
          governanceTokenSymbol: tokenSymbol,
          customAssetAddress,
          recipient,
          amount,
          tokenId,
        }, publicClient as unknown as ReadClient);
        targets = [action.target];
        values = [action.value];
        calldatas = [action.calldata];
        generatedSummary = action.summary;
      } catch (error) {
        const message = error instanceof Error ? error.message : "Unable to prepare this asset transfer.";
        toast({ title: "Invalid transfer", description: message, variant: "destructive" });
        return;
      }
    } else if (proposalType === "domain") {
      if (!POLYGON_NAME_SERVICE_ADDRESS || !publicClient) {
        toast({ title: "Polygon Name Service unavailable", description: "Configure its verified Polygon Mainnet contract address first.", variant: "destructive" });
        return;
      }
      try {
        const label = validateDomainLabel(domainLabel);
        const [registrationPrice, currentOwner, treasuryBalance, registryCode, registryTld] = await Promise.all([
          publicClient.readContract({ address: POLYGON_NAME_SERVICE_ADDRESS, abi: POLYGON_NAME_SERVICE_ABI, functionName: "price", args: [label] } as never) as Promise<bigint>,
          publicClient.readContract({ address: POLYGON_NAME_SERVICE_ADDRESS, abi: POLYGON_NAME_SERVICE_ABI, functionName: "getAddress", args: [label] } as never) as Promise<Address>,
          publicClient.getBalance({ address: treasuryAddress }),
          publicClient.getBytecode({ address: POLYGON_NAME_SERVICE_ADDRESS }),
          publicClient.readContract({ address: POLYGON_NAME_SERVICE_ADDRESS, abi: POLYGON_NAME_SERVICE_ABI, functionName: "tld" } as never) as Promise<string>,
        ]);
        if (!registryCode || registryTld !== POLYGON_NAME_TLD) throw new Error("The configured Polygon Name Service contract failed verification.");
        if (currentOwner !== zeroAddress) throw new Error(`${label}${POLYGON_NAME_TLD} is already registered.`);
        if (treasuryBalance < registrationPrice) throw new Error(`The DAO treasury needs ${formatEther(registrationPrice)} POL to register this name.`);
        targets = [POLYGON_NAME_SERVICE_ADDRESS];
        values = [registrationPrice];
        calldatas = [encodeDomainRegistration(label, domainRecord)];
        generatedSummary = `Register ${label}${POLYGON_NAME_TLD} to DAO treasury ${treasuryAddress}${domainRecord.trim() ? ` with record: ${domainRecord.trim()}` : ""}.`;
      } catch (error) {
        toast({ title: "Invalid domain proposal", description: error instanceof Error ? error.message : "Unable to validate this Polygon name.", variant: "destructive" });
        return;
      }
    } else if (proposalType === "inception") {
      if (!INCEPTION_FACTORY_ADDRESS) {
        toast({ title: "Inception Tokens are not configured", description: "The Polygon Inception factory address is missing from this website deployment.", variant: "destructive" });
        return;
      }
      targets = [INCEPTION_FACTORY_ADDRESS];
      values = [0n];
      calldatas = [INCEPTION_CREATE_CALLDATA];
    } else if (proposalType === "marketplace") {
      if (!NFT_MARKETPLACE_ADDRESS || !publicClient) {
        toast({
          title: "NFT marketplace unavailable",
          description: "Configure VITE_NFT_MARKETPLACE_ADDRESS and connect to Polygon before creating this proposal.",
          variant: "destructive",
        });
        return;
      }

      try {
        const action = buildMarketplaceGovernanceAction({
          actionType: marketplaceAction,
          marketplaceAddress: NFT_MARKETPLACE_ADDRESS,
          treasuryAddress,
          feeRecipient: marketplaceFeeRecipient,
          feeBps: marketplaceFeeBps,
          newOwner: marketplaceNewOwner,
        });
        const marketplaceCode = await publicClient.getBytecode({ address: NFT_MARKETPLACE_ADDRESS });
        if (!marketplaceCode) throw new Error("The configured marketplace address has no contract code on Polygon.");

        if (marketplaceAction !== "withdraw") {
          const currentOwner = await publicClient.readContract({
            address: NFT_MARKETPLACE_ADDRESS,
            abi: NFT_MARKETPLACE_ABI,
            functionName: "owner",
          } as never) as Address;
          if (currentOwner.toLowerCase() !== treasuryAddress.toLowerCase()) {
            throw new Error(`This DAO treasury is not the marketplace owner. The current owner (${currentOwner}) must first transfer ownership to ${treasuryAddress}.`);
          }
        }

        targets = [action.target];
        values = [action.value];
        calldatas = [action.calldata];
        generatedTitle = action.title;
        generatedSummary = action.summary;
      } catch (error) {
        toast({
          title: "Invalid marketplace proposal",
          description: error instanceof Error ? error.message : "Unable to prepare the marketplace action.",
          variant: "destructive",
        });
        return;
      }
    } else if (proposalType === "action") {
      if (!targetAddress || !calldata) {
        toast({ title: "Incomplete action", description: "Target contract and calldata are both required for an executable proposal.", variant: "destructive" });
        return;
      }
      if (!isAddress(targetAddress)) {
        toast({ title: "Invalid target", description: "Enter a valid Polygon contract address.", variant: "destructive" });
        return;
      }
      if (!/^0x([a-fA-F0-9]{2})*$/.test(calldata)) {
        toast({ title: "Invalid calldata", description: "Calldata must be a hexadecimal 0x-prefixed value.", variant: "destructive" });
        return;
      }

      targets = [targetAddress as Address];
      values = [0n];
      calldatas = [calldata as `0x${string}`];
    } else {
      // OpenZeppelin Governor requires at least one operation. Calling this
      // Governance DAO's read-only threshold getter is executable but has no state effect.
      targets = [governorAddress];
      values = [0n];
      calldatas = [GOVERNANCE_NO_OP_CALLDATA];
    }
    
    const proposalTitle = title.trim() || generatedTitle || "Transfer treasury asset";
    const proposalBody = description.trim();
    const fullDescription = generatedSummary
      ? `# ${proposalTitle}\n\n${proposalBody ? `${proposalBody}\n\n` : ""}## Executable action\n${generatedSummary}`
      : `# ${proposalTitle}\n\n${proposalBody}`;

    const created = await onCreateProposal(targets, values, calldatas, fullDescription);
    if (!created) return;

    // Reset form
    setTitle("");
    setDescription("");
    setTargetAddress("");
    setCalldata("");
    setAssetType("native");
    setCustomAssetAddress("");
    setRecipient("");
    setAmount("");
    setTokenId("");
    setDomainLabel("");
    setDomainRecord("");
    setMarketplaceAction("withdraw");
    setMarketplaceFeeRecipient(treasuryAddress);
    setMarketplaceFeeBps("250");
    setMarketplaceNewOwner("");
    setProposalType("action");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-1rem)] w-[calc(100%-1rem)] max-w-3xl overflow-y-auto p-4 sm:max-h-[90vh] sm:p-6">
        <DialogHeader className="pr-8 text-left">
          <DialogTitle className="text-xl sm:text-2xl">Create New Proposal</DialogTitle>
          <DialogDescription className="max-w-2xl leading-relaxed">
            Submit a proposal for DAO members to vote on. You need to hold governance tokens to create a proposal.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="min-w-0 space-y-5">
          <div className="space-y-2">
            <Label>Proposal Type</Label>
            <ToggleGroup
              type="single"
              value={proposalType}
              onValueChange={(value) => value && setProposalType(value as ProposalType)}
              variant="outline"
              className="grid grid-cols-1 gap-2 min-[440px]:grid-cols-2 md:grid-cols-3"
              aria-label="Proposal type"
            >
              <ToggleGroupItem value="transfer" className="h-auto min-h-11 w-full whitespace-normal px-2 py-2 text-center leading-tight">
                Transfer assets
              </ToggleGroupItem>
              <ToggleGroupItem value="action" className="h-auto min-h-11 w-full whitespace-normal px-2 py-2 text-center leading-tight">
                Executable action
              </ToggleGroupItem>
              <ToggleGroupItem value="discussion" className="h-auto min-h-11 w-full whitespace-normal px-2 py-2 text-center leading-tight">
                Discussion only
              </ToggleGroupItem>
              <ToggleGroupItem value="inception" className="h-auto min-h-11 w-full whitespace-normal px-2 py-2 text-center leading-tight">
                Mint DAO Inception Token
              </ToggleGroupItem>
              <ToggleGroupItem value="domain" className="h-auto min-h-11 w-full whitespace-normal px-2 py-2 text-center leading-tight">
                Register domain
              </ToggleGroupItem>
              <ToggleGroupItem value="marketplace" className="h-auto min-h-11 w-full whitespace-normal px-2 py-2 text-center leading-tight">
                NFT marketplace
              </ToggleGroupItem>
            </ToggleGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Proposal Title {proposalTextOptional ? "(optional)" : "*"}</Label>
            <Input
              id="title"
              placeholder="e.g., Allocate funds for marketing campaign"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required={!proposalTextOptional}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description {proposalTextOptional ? "(optional)" : "*"}</Label>
            <Textarea
              id="description"
              placeholder="Provide detailed information about your proposal..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              className="min-h-28 resize-y"
              required={!proposalTextOptional}
            />
          </div>

          {proposalType === "action" && (
            <>
              <div className="space-y-2">
                <Label htmlFor="target">Target Contract *</Label>
                <Input
                  id="target"
                  placeholder="0x..."
                  value={targetAddress}
                  onChange={(e) => setTargetAddress(e.target.value)}
                  required
                />
                <p className="text-sm text-muted-foreground">
                  Contract address to execute transactions on if proposal passes
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="calldata">Calldata *</Label>
                <Input
                  id="calldata"
                  placeholder="0x... (function call data)"
                  value={calldata}
                  onChange={(e) => setCalldata(e.target.value)}
                  required
                />
                <p className="text-sm text-muted-foreground">
                  Transaction data to execute if proposal passes
                </p>
              </div>
            </>
          )}

          {proposalType === "marketplace" && (
            <div className="space-y-4 rounded-lg border p-3 sm:p-4">
              <div className="space-y-2">
                <Label>Marketplace action</Label>
                <Select
                  value={marketplaceAction}
                  onValueChange={(value) => setMarketplaceAction(value as MarketplaceGovernanceActionType)}
                >
                  <SelectTrigger><SelectValue placeholder="Select a marketplace action" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="withdraw">Withdraw DAO proceeds</SelectItem>
                    <SelectItem value="set-fees">Set fee configuration</SelectItem>
                    <SelectItem value="transfer-ownership">Change marketplace owner</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="rounded-md bg-muted/40 p-3 text-xs">
                <p className="text-muted-foreground">Configured marketplace</p>
                {NFT_MARKETPLACE_ADDRESS ? (
                  <p className="mt-1 break-all font-mono">{NFT_MARKETPLACE_ADDRESS}</p>
                ) : (
                  <p className="mt-1 text-destructive">Set VITE_NFT_MARKETPLACE_ADDRESS and rebuild the app.</p>
                )}
                <p className="mt-2 text-muted-foreground">Executing DAO treasury</p>
                <p className="mt-1 break-all font-mono">{treasuryAddress}</p>
              </div>

              {marketplaceAction === "set-fees" && (
                <div className="grid gap-4 sm:grid-cols-[1fr_160px]">
                  <div className="space-y-2">
                    <Label htmlFor="marketplace-fee-recipient">Fee Recipient *</Label>
                    <Input
                      id="marketplace-fee-recipient"
                      placeholder="0x..."
                      value={marketplaceFeeRecipient}
                      onChange={(event) => setMarketplaceFeeRecipient(event.target.value)}
                      required
                    />
                    <Button
                      type="button"
                      variant="link"
                      className="h-auto p-0 text-xs"
                      onClick={() => setMarketplaceFeeRecipient(treasuryAddress)}
                    >
                      Use this DAO treasury
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="marketplace-fee-bps">Fee (bps) *</Label>
                    <Input
                      id="marketplace-fee-bps"
                      type="number"
                      inputMode="numeric"
                      min="0"
                      max="1000"
                      step="1"
                      value={marketplaceFeeBps}
                      onChange={(event) => setMarketplaceFeeBps(event.target.value)}
                      required
                    />
                    <p className="text-xs text-muted-foreground">100 bps = 1%; maximum 1,000 bps.</p>
                  </div>
                </div>
              )}

              {marketplaceAction === "transfer-ownership" && (
                <div className="space-y-2">
                  <Label htmlFor="marketplace-new-owner">New Marketplace Owner *</Label>
                  <Input
                    id="marketplace-new-owner"
                    placeholder="0x..."
                    value={marketplaceNewOwner}
                    onChange={(event) => setMarketplaceNewOwner(event.target.value)}
                    required
                  />
                </div>
              )}

              {marketplaceAction === "withdraw" && (
                <Alert>
                  <AlertTitle>Withdraws only this treasury's balance</AlertTitle>
                  <AlertDescription>
                    The marketplace sends proceeds recorded for the exact DAO treasury address back to that treasury. Execution reverts if its proceeds balance is zero.
                  </AlertDescription>
                </Alert>
              )}

              {marketplaceAction === "set-fees" && (
                <Alert>
                  <AlertTitle>Fee configuration is not ownership</AlertTitle>
                  <AlertDescription>
                    This changes the recipient and rate for future purchases only. The proposal can execute only while this DAO treasury is the marketplace owner.
                  </AlertDescription>
                </Alert>
              )}

              {marketplaceAction === "transfer-ownership" && (
                <Alert variant="destructive">
                  <AlertTitle>This removes the DAO's marketplace authority</AlertTitle>
                  <AlertDescription>
                    After execution, only the new owner can change fees or transfer ownership again. Verify the destination carefully.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {proposalType === "transfer" && (
            <div className="space-y-4 rounded-lg border p-3 sm:p-4">
              <div className="space-y-2">
                <Label>Asset</Label>
                <Select value={assetType} onValueChange={(value) => setAssetType(value as TransferAssetType)}>
                  <SelectTrigger><SelectValue placeholder="Select an asset" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="native">POL (native treasury balance)</SelectItem>
                    <SelectItem value="governance">{tokenSymbol} governance token</SelectItem>
                    <SelectItem value="erc20">Other ERC-20 token</SelectItem>
                    <SelectItem value="erc721">ERC-721 NFT</SelectItem>
                    <SelectItem value="erc1155">ERC-1155 token</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {assetType !== "native" && assetType !== "governance" && (
                <div className="space-y-2">
                  <Label htmlFor="transfer-asset-address">Asset Contract *</Label>
                  <Input id="transfer-asset-address" placeholder="0x..." value={customAssetAddress} onChange={(event) => setCustomAssetAddress(event.target.value)} required />
                </div>
              )}

              {(assetType === "erc721" || assetType === "erc1155") && (
                <div className="space-y-2">
                  <Label htmlFor="transfer-token-id">Token ID *</Label>
                  <Input id="transfer-token-id" inputMode="numeric" placeholder="0" value={tokenId} onChange={(event) => setTokenId(event.target.value)} required />
                </div>
              )}

              {assetType !== "erc721" && (
                <div className="space-y-2">
                  <Label htmlFor="transfer-amount">Amount *</Label>
                  <Input id="transfer-amount" inputMode="decimal" placeholder={assetType === "native" ? "Amount in POL" : "Token amount"} value={amount} onChange={(event) => setAmount(event.target.value)} required />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="transfer-recipient">Recipient Wallet *</Label>
                <Input id="transfer-recipient" placeholder="0x..." value={recipient} onChange={(event) => setRecipient(event.target.value)} required />
              </div>

              <Alert>
                <AlertTitle>Executed by the DAO treasury</AlertTitle>
                <AlertDescription>
                  The app checks the treasury's current balance or NFT ownership before encoding the proposal. Balances can still change before execution.
                </AlertDescription>
              </Alert>
            </div>
          )}

          {proposalType === "inception" && (
            <Alert className="min-w-0">
              <Archive className="h-4 w-4" />
              <AlertTitle>Mint an Inception Token to this DAO</AlertTitle>
              <AlertDescription className="space-y-2">
                <p>When the proposal is executed, the Polygon Inception factory will mint the vault ownership NFT to the treasury that executes it.</p>
                <p className="break-all font-mono text-xs">Treasury: {treasuryAddress}</p>
                {INCEPTION_FACTORY_ADDRESS ? <p className="break-all font-mono text-xs">Target: {INCEPTION_FACTORY_ADDRESS}</p> : <p className="text-destructive">The Inception factory is not configured.</p>}
              </AlertDescription>
            </Alert>
          )}

          {proposalType === "domain" && (
            <div className="space-y-4 rounded-lg border p-3 sm:p-4">
              <div className="space-y-2">
                <Label htmlFor="dao-domain">Domain label *</Label>
                <div className="flex items-center gap-2"><Input id="dao-domain" className="min-w-0" value={domainLabel} onChange={(event) => setDomainLabel(event.target.value)} placeholder="community" required /><span className="shrink-0 text-sm text-muted-foreground">{POLYGON_NAME_TLD}</span></div>
              </div>
              <div className="space-y-2"><Label htmlFor="dao-domain-record">Initial record</Label><Textarea id="dao-domain-record" value={domainRecord} onChange={(event) => setDomainRecord(event.target.value)} placeholder="ipfs://…, https://…, or DAO metadata" rows={3} /><p className="text-xs text-muted-foreground">Maximum {MAX_DOMAIN_RECORD_BYTES} UTF-8 bytes.</p></div>
              <Alert><AlertTitle>Owned by the DAO treasury</AlertTitle><AlertDescription>The proposal includes the live registration price as native POL. Execution will revert safely if another wallet registers the name first.</AlertDescription></Alert>
              {!POLYGON_NAME_SERVICE_ADDRESS && <p className="text-sm text-destructive">VITE_POLYGON_NAME_SERVICE_ADDRESS is not configured.</p>}
            </div>
          )}

          <div className="flex flex-col-reverse gap-2 border-t pt-4 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading} className="w-full sm:w-auto">
              {isLoading ? statusLabel || "Creating..." : "Create Proposal"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
